import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteUser } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { ApiException } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import newRandomEmail from 'secure-random-password';
import axios from 'axios';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Shuffle from '@mui/icons-material/Shuffle';

interface DeleteUserDialogProps {
  domains: string[];
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
  primaryDomain: string;
}

export default function DeleteUserDialog({
  open,
  handleClose,
  userEmail,
  domains,
  changeLogRoles,
  primaryDomain,
}: DeleteUserDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const [newEmailCheckBox, setNewEmailCheckBox] = useState(false);
  const [requiredBoxes, setRequiredBoxes] = useState({
    documents: false,
    emails: false,
  });
  const { documents, emails } = requiredBoxes;
  const [domain, setDomain] = useState(
    primaryDomain !== '' ? primaryDomain : domains[0]
  );
  const [hasError, setHasError] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const { setSnackbar } = useStore();
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteUser(
        userEmail,
        newEmail.length > 0 ? newEmail + '@' + domain : newEmail,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User deleted successfully!',
      });

      createEvent(analytics.user_management.user_details.delete_user, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.sections.userDetails,
        immediatelyReusable: newEmail.length > 0 ? true : false,
      });
      queryClient.invalidateQueries({ queryKey: [UserKeys.UserList] });
      setChangeLogMessage('');
      setHasError(false);
      setDomain(primaryDomain !== '' ? primaryDomain : domains[0]);
      setNewEmail('');
      setNewEmailCheckBox(false);
      setRequiredBoxes({
        documents: false,
        emails: false,
      });
      navigate('..', { relative: 'path', replace: true });
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message?.includes('409')) {
          setHasError(true);
        } else {
          handleErrors({
            error,
            snackbarMessage:
              'There was an issue deleting this user. Please refresh and try again.',
          });
        }
      }
    },
    onSettled: () => {
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.DeleteUsers.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequiredBoxes({
      ...requiredBoxes,
      [event.target.name]: event.target.checked,
    });
  };

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setHasError(false);
        setDomain(primaryDomain !== '' ? primaryDomain : domains[0]);
        setNewEmail('');
        setNewEmailCheckBox(false);
        setRequiredBoxes({
          documents: false,
          emails: false,
        });
        handleClose();
      }}
      title={`Delete User`}
      dialogContent={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography>
            Delete <strong>{userEmail}</strong>?
          </Typography>

          <Box>
            <FormGroup sx={{ ml: 2 }}>
              <FormControlLabel
                sx={{ width: 'fit-content' }}
                control={
                  <Checkbox
                    checked={newEmailCheckBox}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const generatedEmail = newRandomEmail.randomString({
                          length: 16,
                          characters: [
                            newRandomEmail.lower,
                            newRandomEmail.digits,
                          ],
                        });
                        setNewEmail(generatedEmail);
                        setNewEmailCheckBox(true);
                      } else {
                        setNewEmail('');
                        setNewEmailCheckBox(false);
                      }
                    }}
                  />
                }
                label={
                  <Typography>Make username immediately reusable.</Typography>
                }
              />
            </FormGroup>
            {newEmailCheckBox ? (
              <>
                <Stack
                  alignItems="baseline"
                  width="100%"
                  flexDirection={'row'}
                  sx={{ mt: 2, mb: 2 }}
                  gap={1}
                >
                  <TextField
                    id="newEmail"
                    label="New Email"
                    error={hasError}
                    helperText={hasError ? 'User Email already taken.' : ''}
                    fullWidth
                    variant="standard"
                    value={newEmail}
                    onChange={(event) => setNewEmail(event.target.value)}
                  />
                  <Tooltip title="Create Email" arrow>
                    <span>
                      <IconButton
                        onClick={() => {
                          const generatedEmail = newRandomEmail.randomString({
                            length: 16,
                            characters: [
                              newRandomEmail.lower,
                              newRandomEmail.digits,
                            ],
                          });

                          setNewEmail(generatedEmail);
                        }}
                      >
                        <Shuffle />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <TextField
                    select
                    fullWidth
                    variant="standard"
                    value={domain}
                    onChange={(event) => setDomain(event.target.value)}
                  >
                    {domains.map((domain) => (
                      <MenuItem value={domain} key={domain}>
                        {domain}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Grow in={newEmailCheckBox}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Normally, a username cannot be reused immediately after
                    being deleted. Google prevents the reuse of the address
                    while they are busy deleting all your files. If you allow
                    gPanel to rename the account before you delete it,{' '}
                    {userEmail} can be immediately used elsewhere (as a group,
                    user or alias email). Please note that recreating a new user
                    account too soon can result in the incomplete deletion of
                    the user's documents, calendars, sites and other files.
                  </Alert>
                </Grow>
              </>
            ) : null}
          </Box>

          <Divider sx={{ mx: -3 }} />

          <Box>
            <Typography sx={{ mb: 2 }}>
              To proceed, please acknowledge the following:
            </Typography>
            <FormControl component="fieldset">
              <FormGroup sx={{ ml: 2, gap: 1 }}>
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={
                    <Checkbox
                      checked={documents}
                      onChange={handleChange}
                      name="documents"
                    />
                  }
                  label={
                    <Typography>
                      I understand that all <strong>documents</strong> owned by
                      this user will be inaccessible.
                    </Typography>
                  }
                />
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={
                    <Checkbox
                      checked={emails}
                      onChange={handleChange}
                      name="emails"
                    />
                  }
                  label={
                    <Typography>
                      I understand that all <strong>emails</strong> and{' '}
                      <strong>calendar events</strong> will be permanently
                      deleted.
                    </Typography>
                  }
                />
              </FormGroup>
            </FormControl>
          </Box>

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete User',
        color: 'error',
        action: handleSubmit,
        loading: isPending,
        disabled:
          isPending ||
          requiredBoxes.documents === false ||
          requiredBoxes.emails === false,
      }}
    />
  );
}
